// Bill Pay supporting tabs (spec §6.4 AutoPay, §6.5 Recent & Billers, §6.6 Help).
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {
  payments as listPayments,
  savedBillers,
  mandates as listMandates,
  complaints as listComplaints,
  createMandate,
  deleteMandate,
  setMandatePaused,
  skipNextDebit,
  approveMandateDebit,
  deleteBiller,
  renameBiller,
  raiseComplaint,
} from '@/api/billPay';
import { BILLERS, billerById, autopayOtpLimitRupees } from '@/mock/billPayCatalogue';
import { runBankClock } from '@/mock/phase4';
import type { BillComplaint, BillPayment, Mandate, SavedBiller } from '@/types/phase4.types';
import { usePaymentAuth } from '@/pages/Pay/components/usePaymentAuth';
import PaymentAuthSheets from '@/pages/Pay/components/PaymentAuthSheets';
import { formatIndianCurrency } from '@/utils/currency';
import { formatLong } from '@/mock/clock';
import ListState from '../components/ListState';
import '../phase4.css';

const STATUS_CHIP: Record<BillPayment['status'], string> = {
  SUCCESS: 'ok', PENDING: 'warn', FAILED: 'bad',
};

const BillPayTabs = () => {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const title = tab === 'autopay' ? 'AutoPay' : tab === 'recent' ? 'Recent' : tab === 'billers' ? 'Billers' : 'Help';
  return (
    <div className="nova-p4-page">
      <div className="nova-p4-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/dashboard')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>{title}</h2>
        <span className="nova-p4-bc-badge">Bharat Connect</span>
      </div>
      {tab === 'autopay' && <AutoPay />}
      {tab === 'recent' && <Recent />}
      {tab === 'billers' && <Billers />}
      {tab === 'help' && <Help />}
    </div>
  );
};

// ---------- AutoPay (§6.4) ----------

const AutoPay = () => {
  const [items, setItems] = useState<Mandate[] | null>(null);
  const [billers, setBillers] = useState<SavedBiller[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [billerId, setBillerId] = useState('');
  const [rule, setRule] = useState<'ON_DUE' | 'BEFORE_DUE' | 'FIXED_DAY'>('ON_DUE');
  const [beforeDays, setBeforeDays] = useState(1);
  const [fixedDay, setFixedDay] = useState(1);
  const [fixedAmount, setFixedAmount] = useState('');
  const [cap, setCap] = useState('');
  const [error, setError] = useState('');

  const load = () => {
    runBankClock();
    listMandates().then(setItems).catch(() => setItems([]));
    savedBillers().then(setBillers).catch(() => setBillers([]));
  };
  useEffect(load, []);

  const auth = usePaymentAuth(async () => {
    try {
      await createMandate({
        billerId,
        nickname: billers.find((b) => b.id === billerId)?.nickname || 'Biller',
        rule,
        beforeDays: rule === 'BEFORE_DUE' ? beforeDays : undefined,
        fixedDay: rule === 'FIXED_DAY' ? fixedDay : undefined,
        fixedAmountRupees: rule === 'FIXED_DAY' ? Number(fixedAmount) : undefined,
        capRupees: Number(cap),
      });
      toast.success('AutoPay set');
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not set AutoPay.');
    }
  });

  const submit = async () => {
    setError('');
    if (!billerId) return setError('Pick a saved biller.'), undefined;
    if (!cap || Number(cap) <= 0) return setError('Set a maximum amount for AutoPay.'), undefined;
    if (rule === 'FIXED_DAY' && (!fixedAmount || Number(fixedAmount) <= 0 || Number(fixedAmount) > Number(cap))) {
      return setError('The fixed amount must be more than ₹0 and within the cap.'), undefined;
    }
    const e = await auth.start('NOVABANK', 'Register AutoPay (e-mandate)');
    if (e) setError(e);
    return undefined;
  };

  const otpAction = usePaymentAuth(async () => load());

  return (
    <>
      <div className="nova-p4-banner blue">Charges: ₹0 — no charges for e-mandates. Registering needs MPIN + OTP; edit, pause and delete need OTP.</div>

      {!showForm && (
        <button type="button" className="nova-p4-primary" style={{ marginBottom: 16 }} onClick={() => setShowForm(true)} disabled={billers.length === 0}>
          Set up AutoPay
        </button>
      )}
      {billers.length === 0 && <div className="nova-p4-empty">Save a biller first — AutoPay needs one.</div>}

      {showForm && (
        <div className="nova-p4-card">
          <h3>New AutoPay</h3>
          <div className="nova-p4-field">
            <label htmlFor="ap_biller">Saved biller</label>
            <select id="ap_biller" className="form-select" value={billerId} onChange={(e) => setBillerId(e.target.value)}>
              <option value="">Pick a biller</option>
              {billers.map((b) => <option key={b.id} value={b.id}>{b.nickname || billerById(b.billerId)?.name}</option>)}
            </select>
          </div>
          <div className="nova-p4-field">
            <label htmlFor="ap_rule">When to pay</label>
            <select id="ap_rule" className="form-select" value={rule} onChange={(e) => setRule(e.target.value as typeof rule)}>
              <option value="ON_DUE">Full bill on the due date</option>
              <option value="BEFORE_DUE">Full bill before the due date</option>
              <option value="FIXED_DAY">A fixed amount on a fixed day</option>
            </select>
          </div>
          {rule === 'BEFORE_DUE' && (
            <div className="nova-p4-field">
              <label htmlFor="ap_before">Days before the due date</label>
              <select id="ap_before" className="form-select" value={beforeDays} onChange={(e) => setBeforeDays(Number(e.target.value))}>
                {[1, 2, 3, 5, 7].map((d) => <option key={d} value={d}>{d} day{d > 1 ? 's' : ''} before</option>)}
              </select>
            </div>
          )}
          {rule === 'FIXED_DAY' && (
            <>
              <div className="nova-p4-field">
                <label htmlFor="ap_day">Day of month (1–28)</label>
                <input id="ap_day" className="form-control" type="number" min={1} max={28} value={fixedDay} onChange={(e) => setFixedDay(Number(e.target.value))} />
              </div>
              <div className="nova-p4-field">
                <label htmlFor="ap_amt">Fixed amount</label>
                <input id="ap_amt" className="form-control" type="number" min={1} value={fixedAmount} onChange={(e) => setFixedAmount(e.target.value)} />
              </div>
            </>
          )}
          <div className="nova-p4-field">
            <label htmlFor="ap_cap">Maximum amount (cap) — required</label>
            <input id="ap_cap" className="form-control" type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} />
            <div className="hint">Bills above this are skipped. A debit you did not expect? Report fraud.</div>
          </div>
          {error && <div className="nova-p4-banner red">{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="nova-p4-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="button" className="nova-p4-primary" onClick={() => void submit()}>Register (MPIN + OTP)</button>
          </div>
        </div>
      )}

      <ListState data={items} error={null} onRetry={load} empty={<span>No AutoPay yet.</span>}>
        {(list) => list.map((m) => {
          const biller = billerById(billers.find((b) => b.id === m.billerId)?.billerId ?? '');
          const limit = biller ? autopayOtpLimitRupees(biller.categoryId) : 15000;
          return (
            <div key={m.id} className="nova-p4-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div className="nova-p4-row-title">{m.nickname}</div>
                  <div className="nova-p4-row-sub">
                    {m.rule === 'ON_DUE' ? 'Full bill on the due date' : m.rule === 'BEFORE_DUE' ? `${m.beforeDays} day(s) before due` : `${formatIndianCurrency(m.fixedAmountRupees ?? 0)} on day ${m.fixedDay}`} · cap {formatIndianCurrency(m.capRupees)} · no OTP up to {formatIndianCurrency(limit)}
                  </div>
                </div>
                {m.paused && <span className="nova-p4-status warn">Paused</span>}
              </div>
              {m.pendingApprovalId && (
                <div className="nova-p4-banner amber" style={{ marginTop: 10 }}>
                  A debit needs your approval.
                  <button type="button" className="nova-p4-link" onClick={() => void approveMandateDebit(m.id).then(() => { toast.success('Approved'); load(); })}>Approve with OTP</button>
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                <button type="button" className="nova-p4-link" onClick={() => void setMandatePaused(m.id, !m.paused).then(load)}>{m.paused ? 'Resume' : 'Pause'}</button>
                <button type="button" className="nova-p4-link" onClick={() => void skipNextDebit(m.id).then(() => { toast.success('Next debit skipped'); load(); })}>Skip next</button>
                <button type="button" className="nova-p4-link" style={{ color: '#c0392b' }} onClick={() => void deleteMandate(m.id).then(load)}>Delete</button>
              </div>
            </div>
          );
        })}
      </ListState>

      <PaymentAuthSheets authState={auth.authState} approvalLine="Register AutoPay" pinError={auth.pinError} pinResetKey={auth.pinResetKey} otpExpiry={auth.otpExpiry} otpSessionKey={auth.otpSessionKey} onCompletePin={auth.completePin} onVerifyOtp={auth.verifyOtp} onResendOtp={auth.resendOtp} onClose={auth.close} />
      <PaymentAuthSheets authState={otpAction.authState} approvalLine="Confirm" pinError={otpAction.pinError} pinResetKey={otpAction.pinResetKey} otpExpiry={otpAction.otpExpiry} otpSessionKey={otpAction.otpSessionKey} onCompletePin={otpAction.completePin} onVerifyOtp={otpAction.verifyOtp} onResendOtp={otpAction.resendOtp} onClose={otpAction.close} />
    </>
  );
};

// ---------- Recent (§6.5) ----------

const Recent = () => {
  const [items, setItems] = useState<BillPayment[] | null>(null);
  const [status, setStatus] = useState('');
  const [period, setPeriod] = useState<'30' | '90' | 'all'>('90');
  const load = () => { runBankClock(); listPayments().then(setItems).catch(() => setItems([])); };
  useEffect(load, []);

  const now = Date.now();
  const filtered = (items ?? []).filter((p) => {
    if (status && p.status !== status) return false;
    if (period !== 'all' && now - p.at > Number(period) * 24 * 3_600_000) return false;
    return true;
  });

  return (
    <>
      <div className="nova-p4-chip-row">
        <select className="form-select form-select-sm w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">All statuses</option><option value="SUCCESS">Success</option><option value="PENDING">Pending</option><option value="FAILED">Failed</option>
        </select>
        <select className="form-select form-select-sm w-auto" value={period} onChange={(e) => setPeriod(e.target.value as typeof period)} aria-label="Period">
          <option value="30">30 days</option><option value="90">90 days</option><option value="all">All</option>
        </select>
      </div>
      <ListState data={filtered as BillPayment[]} error={null} onRetry={load} empty={<span>No payments in this period.</span>}>
        {(list) => list.map((p) => (
          <div key={p.id} className="nova-p4-row">
            <span className="nova-p4-row-text">
              <span className="nova-p4-row-title">{p.billerName}</span>
              <span className="nova-p4-row-sub">{formatLong(p.at)} · {p.bcId}{p.autopay ? ' · AutoPay' : ''}</span>
            </span>
            <span style={{ textAlign: 'right' }}>
              <span className="nova-p4-amount">{formatIndianCurrency(p.amountRupees)}</span><br />
              <span className={`nova-p4-status ${STATUS_CHIP[p.status]}`}>{p.status === 'PENDING' ? 'Awaiting biller confirmation' : p.status === 'FAILED' ? 'Refund initiated' : 'Success'}</span>
            </span>
          </div>
        ))}
      </ListState>
    </>
  );
};

// ---------- Billers (§6.5) ----------

const Billers = () => {
  const [items, setItems] = useState<SavedBiller[] | null>(null);
  const load = () => { runBankClock(); savedBillers().then(setItems).catch(() => setItems([])); };
  useEffect(load, []);
  const navigate = useNavigate();

  return (
    <ListState data={items} error={null} onRetry={load} empty={<span>No saved billers yet. Pay a bill and save the biller.</span>}>
      {(list) => list.map((b) => {
        const biller = billerById(b.billerId);
        return (
          <div key={b.id} className="nova-p4-card">
            <div className="nova-p4-row-title">{b.nickname || biller?.name}</div>
            <div className="nova-p4-row-sub">{biller?.name} · {Object.values(b.params).join(' · ')}{b.lastPaidAt ? ` · last paid ${formatLong(b.lastPaidAt)}` : ''}</div>
            <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
              <button type="button" className="nova-p4-link" onClick={() => navigate(`/pay/bills/category/${biller?.categoryId ?? 'others'}`, { state: { savedBillerId: b.id } })}>Pay now</button>
              <button type="button" className="nova-p4-link" onClick={() => { const n = window.prompt('Nickname (24 characters)', b.nickname); if (n != null) void renameBiller(b.id, n.slice(0, 24)).then(load); }}>Edit nickname</button>
              <button type="button" className="nova-p4-link" style={{ color: '#c0392b' }} onClick={() => void deleteBiller(b.id).then(() => { toast.success('Biller deleted'); load(); })}>Delete</button>
            </div>
          </div>
        );
      })}
    </ListState>
  );
};

// ---------- Help (§6.6) ----------

const Help = () => {
  const [items, setItems] = useState<BillComplaint[] | null>(null);
  const [payments, setPayments] = useState<BillPayment[]>([]);
  const [kind, setKind] = useState<'TRANSACTION' | 'SERVICE'>('TRANSACTION');
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('Debited but bill not updated');
  const [description, setDescription] = useState('');
  const load = () => {
    void listComplaints().then(setItems).catch(() => setItems([]));
    void listPayments().then(setPayments).catch(() => setPayments([]));
  };
  useEffect(() => {
    const refresh = () => { runBankClock(); load(); };
    refresh();
    const timer = window.setInterval(refresh, 2000);
    return () => window.clearInterval(timer);
  }, []);

  const selectedPayment = payments.find((payment) => payment.bcId === target);
  const statusLabel = (status: BillComplaint['status']): string => {
    if (status === 'ASSIGNED') return 'Assigned to biller';
    if (status === 'RESOLVED') return 'Resolved';
    return 'Registered';
  };
  const submit = async () => {
    if (!target) {
      toast.error(kind === 'TRANSACTION' ? 'Select a payment.' : 'Select a biller.');
      return;
    }
    if (!description.trim() || (kind === 'SERVICE' && description.trim().length < 10)) {
      toast.error(kind === 'SERVICE' ? 'Description must be at least 10 characters.' : 'Enter a description.');
      return;
    }
    try {
      await raiseComplaint({ kind, target, reason: kind === 'TRANSACTION' ? reason : 'Service complaint', description: description.trim() });
      toast.success('Complaint registered');
      setDescription('');
      setTarget('');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not register the complaint.');
    }
  };

  return (
    <>
      <div className="nova-p4-card">
        <h3>Raise a complaint</h3>
        <div className="nova-p4-field">
          <label htmlFor="h_kind">Complaint type</label>
          <select id="h_kind" className="form-select" value={kind} onChange={(e) => { setKind(e.target.value as typeof kind); setTarget(''); }}>
            <option value="TRANSACTION">Transaction complaint</option>
            <option value="SERVICE">Service complaint</option>
          </select>
        </div>
        <div className="nova-p4-field">
          <label htmlFor="h_target">{kind === 'TRANSACTION' ? 'Payment' : 'Biller'}</label>
          <select id="h_target" className="form-select" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Select {kind === 'TRANSACTION' ? 'a payment' : 'a biller'}</option>
            {kind === 'TRANSACTION'
              ? payments.map((payment) => <option key={payment.id} value={payment.bcId}>{payment.bcId} · {payment.billerName} · {formatIndianCurrency(payment.amountRupees)}</option>)
              : BILLERS.map((biller) => <option key={biller.id} value={biller.name}>{biller.name}</option>)}
          </select>
          {kind === 'TRANSACTION' && selectedPayment && <div className="nova-p4-row-sub">Selected payment: {selectedPayment.billerName} · {formatLong(selectedPayment.at)}</div>}
        </div>
        {kind === 'TRANSACTION' && (
          <div className="nova-p4-field">
            <label htmlFor="h_reason">Reason</label>
            <select id="h_reason" className="form-select" value={reason} onChange={(e) => setReason(e.target.value)}>
              {['Debited but bill not updated', 'Duplicate payment', 'Wrong amount', 'Biller not updated', 'Other'].map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
        )}
        <div className="nova-p4-field">
          <label htmlFor="h_desc">Description</label>
          <textarea id="h_desc" className="form-control" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={kind === 'SERVICE' ? 'Describe the service issue (10+ characters)' : 'Tell us what happened'} />
          {kind === 'SERVICE' && <div className="nova-p4-row-sub">Minimum 10 characters</div>}
        </div>
        <button type="button" className="nova-p4-primary" onClick={() => void submit()}>Submit complaint</button>
      </div>

      <ListState data={items} error={null} onRetry={load} empty={<span>No complaints yet.</span>}>
        {(list) => list.map((c) => (
          <div key={c.id} className="nova-p4-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div className="nova-p4-row-title">{c.kind === 'TRANSACTION' ? c.reason : 'Service complaint'}</div>
                <div className="nova-p4-row-sub">{c.target} · {formatLong(c.createdAt)}</div>
              </div>
              <span className={`nova-p4-status ${c.status === 'RESOLVED' ? 'ok' : c.status === 'ASSIGNED' ? 'warn' : 'info'}`}>{statusLabel(c.status)}</span>
            </div>
            <div className="nova-p4-row-sub" style={{ marginTop: 8 }}>Reference: <strong>{c.reference}</strong></div>
            <div className="nova-p4-row-sub" style={{ marginTop: 6 }}>{c.description}</div>
            {c.resolution && <div className="nova-p4-banner green" style={{ marginTop: 8 }}>{c.resolution}</div>}
          </div>
        ))}
      </ListState>
    </>
  );
};

export default BillPayTabs;
